/**
 * Uprawnienia = czynność × zakres danych (docs/06-uprawnienia.md).
 * Kody są stabilnymi identyfikatorami zapisywanymi w bazie — nie zmieniać bez migracji.
 */

/**
 * Zakresy danych. F („firmy pracownika”) nie nadaje się w rolach ani w wyjątkach (od 02.11.2026): powstaje na serwerze,
 * gdy pracownik działa w wybranych firmach, z zakresu W uprawnień oznaczonych jako dane firmowe (`isCompanyPermission`).
 */
export const SCOPES = ['W', 'F', 'E', 'P', 'O'] as const;
export type Scope = (typeof SCOPES)[number];
/** Zakresy do wyboru w edytorze ról i w wyjątkach pracownika. */
export const EDITABLE_SCOPES = ['W', 'E', 'P', 'O'] as const satisfies readonly Scope[];

export const SCOPE_LABELS: Record<Scope, string> = {
  W: 'wszystkie',
  F: 'firmy pracownika',
  E: 'ekipy',
  P: 'przypisane',
  O: 'własne',
};

export interface PermissionDef {
  code: string;
  group: string;
  label: string;
  /** zakresy, które mają sens dla tego uprawnienia (do wyboru w edytorze) */
  scopes: readonly Scope[];
  /** dane firmowe: przy pracowniku w wybranych firmach „wszystkie” oznacza „w jego firmach” */
  company: boolean;
}

/**
 * Zakresy obsługiwane przez serwer dla uprawnień (poza W, które ma każde) — reszta uprawnień tylko W.
 * Zmiana: przejrzeć serwisy (zakres w zapytaniu) i migrację danych ról.
 */
const EXTRA_SCOPES: Record<string, string> = {
  'klienci.podglad': 'P',
  'instalacje.podglad': 'P',
  'urzadzenia.dodawanie': 'P',
  'urzadzenia.edycja': 'P',
  'sprawy.tworzenie': 'O',
  'sprawy.edycja': 'O',
  'zakresy.zmiana_firmy': 'O',
  'zakresy.przegrana': 'O',
  'dokumenty.dodawanie': 'O',
  'sprawy.nadanie_biegu': 'O',
  'sprawy.wstrzymanie': 'O',
  'sprawy.zamkniecie': 'O',
  'sprawy.wznowienie': 'O',
  'obieg.przydzielanie': 'O',
  'oferty.podglad': 'O',
  'oferty.edycja': 'O',
  'oferty.wydanie': 'O',
  'oferty.udostepnianie': 'O',
  'oferty.wycofanie': 'O',
  'oferty.akceptacja_reczna': 'O',
  'katalog.podglad': 'P',
  'ceny.marza': 'O',
  'finanse.podglad': 'O',
  'finanse.wplaty': 'O',
  'faktury.podglad': 'O',
  'zapotrzebowania.podglad': 'O',
  'zapotrzebowania.zglaszanie': 'P',
  'kalendarz.podglad': 'EO',
  'planowanie.edycja': 'O',
  'nieobecnosci.wniosek': 'O',
  'zadania.podglad': 'PO',
  'zadania.status': 'P',
  'zadania.dokumentacja': 'PO',
  'zadania.materialy': 'P',
  'zadania.blokada': 'PO',
  'protokoly.podpis': 'P',
  'protokoly.wysylka': 'O',
  'serwis.podglad': 'P',
  'serwis.tworzenie': 'P',
  'flota.podglad': 'E',
  'flota.dostepnosc': 'E',
  'audyt.podglad': 'O',
};

/** Dane firmowe — prefiksy i pojedyncze kody (serwisy obsługują zawężenie do firm pracownika). */
const COMPANY_PREFIXES = ['umowy.', 'finanse.', 'faktury.', 'zakupy.', 'zapotrzebowania.'];
const COMPANY_CODES = [
  'magazyn.podglad',
  'magazyn.ruchy',
  'magazyn.korekty',
  'magazyn.rezerwacje',
  'magazyn.kompletacja',
  'dostawcy.edycja',
  'flota.podglad',
  'flota.zarzadzanie',
  'flota.koszty',
  'flota.import',
  'flota.przypomnienia',
  'zestawienia.podglad',
  'oferty.wydanie_ponizej_progu',
];
const isCompanyCode = (code: string) => COMPANY_CODES.includes(code) || COMPANY_PREFIXES.some((p) => code.startsWith(p));

const g = (group: string, items: [string, string][]): PermissionDef[] =>
  items.map(([code, label]) => ({
    code,
    group,
    label,
    scopes: ['W', ...((EXTRA_SCOPES[code] ?? '').split('').filter(Boolean) as Scope[])],
    company: isCompanyCode(code),
  }));

/**
 * Moduły lewego menu — „grube” uprawnienie: bez niego pozycja menu jest ukryta,
 * a API odrzuca ekrany i operacje modułu (listy, tablice, ustawienia).
 * Rekordy otwierane z innych modułów (karta sprawy z „Moich zadań”) dalej sprawdzają uprawnienia szczegółowe.
 * Pulpit jest dostępny dla każdego konta biurowego (bez uprawnienia).
 */
export interface ModuleDef {
  /** kod modułu; uprawnienie ma postać `modul.<kod>` */
  code: string;
  label: string;
  /** ścieżka w panelu */
  path: string;
}

export const MODULES: readonly ModuleDef[] = [
  { code: 'moje_zadania', label: 'Moje zadania', path: '/zadania' },
  { code: 'moj_kalendarz', label: 'Mój kalendarz', path: '/moj-kalendarz' },
  { code: 'moje_sprawy', label: 'Moje sprawy', path: '/moje-sprawy' },
  { code: 'sprawy', label: 'Sprawy', path: '/sprawy' },
  { code: 'klienci', label: 'Klienci', path: '/klienci' },
  { code: 'katalog', label: 'Katalog', path: '/katalog' },
  { code: 'planowanie', label: 'Planowanie', path: '/planowanie' },
  { code: 'kalendarz', label: 'Kalendarz', path: '/kalendarz' },
  { code: 'ekipy', label: 'Ekipy', path: '/ekipy' },
  { code: 'pracownicy', label: 'Pracownicy', path: '/pracownicy' },
  { code: 'serwis', label: 'Zgłoszenia i przeglądy', path: '/serwis' },
  { code: 'instalacje', label: 'Instalacje', path: '/instalacje' },
  { code: 'magazyn', label: 'Magazyn', path: '/magazyn/pulpit' },
  { code: 'finanse', label: 'Finanse', path: '/finanse' },
  { code: 'zestawienia', label: 'Zestawienia', path: '/zestawienia' },
  { code: 'firmy', label: 'Firmy', path: '/ustawienia/firmy' },
  { code: 'numeracja', label: 'Numeracja', path: '/ustawienia/numeracja' },
  { code: 'procesy', label: 'Branże i procesy', path: '/ustawienia/procesy' },
  { code: 'obieg', label: 'Zadania w sprawach (ustawienia)', path: '/ustawienia/zadania' },
  { code: 'szablony', label: 'Szablony dokumentów', path: '/dokumenty/szablony' },
  { code: 'role', label: 'Role i uprawnienia', path: '/ustawienia/role' },
  { code: 'historia', label: 'Historia operacji', path: '/historia' },
  /** moduł opcjonalny (docs/19) — przy wyłączonym module uprawnienia `flota.*` są odbierane w profilu */
  { code: 'flota', label: 'Flota', path: '/flota' },
];

export const MODULE_PREFIX = 'modul.';
/**
 * Moduły „moje” — praca danej osoby (zadania, kalendarz, sprawy, w których jest opiekunem). Domyślnie w każdej roli,
 * także nowo utworzonej (03.11.2026); w edytorze ról można je odebrać. „Mój samochód” nie jest modułem — widać go
 * przy przypisanym aucie.
 */
export const PERSONAL_MODULES: readonly string[] = ['moje_zadania', 'moj_kalendarz', 'moje_sprawy'];
export const modulePermission = (code: string) => `${MODULE_PREFIX}${code}`;
export const isModulePermission = (permission: string) => permission.startsWith(MODULE_PREFIX);

export const PERMISSIONS: readonly PermissionDef[] = [
  ...g(
    'Moduły (widoczność w menu)',
    MODULES.map((m) => [modulePermission(m.code), m.label] as [string, string]),
  ),
  ...g('Klienci i obiekty', [
    ['klienci.podglad', 'Podgląd kontrahentów, kontaktów, lokalizacji'],
    ['klienci.edycja', 'Tworzenie i edycja kontrahentów, lokalizacji'],
    ['klienci.scalanie', 'Scalanie duplikatów'],
    ['klienci.eksport', 'Eksport danych klientów'],
    ['klienci.korespondencja', 'Korespondencja z klientem: odpowiadanie, nowe wiadomości, przypinanie do spraw, powiadomienia o wiadomościach'],
    ['instalacje.podglad', 'Podgląd instalacji i kart urządzeń'],
    ['urzadzenia.dodawanie', 'Dodanie urządzenia / numeru seryjnego'],
    ['urzadzenia.edycja', 'Edycja kart urządzeń i gwarancji'],
  ]),
  ...g('Sprawy i sprzedaż', [
    ['sprawy.podglad', 'Podgląd spraw'],
    ['sprawy.tworzenie', 'Tworzenie zapytania / sprawy'],
    ['sprawy.edycja', 'Edycja sprawy, kroków procesu, zakresów'],
    ['sprawy.zmiana_opiekuna', 'Zmiana opiekuna sprawy'],
    ['zakresy.zmiana_firmy', 'Zmiana firmy zakresu (bez wydanych dokumentów)'],
    ['zakresy.przegrana', 'Oznaczenie zakresu jako przegranego'],
    ['dokumenty.dodawanie', 'Dodawanie i usuwanie dokumentów w sprawie'],
  ]),
  ...g('Obieg spraw i zadania', [
    ['sprawy.przejmowanie', 'Przejmowanie nowych spraw jako opiekun'],
    ['sprawy.nadanie_biegu', 'Weryfikacja wstępna: nadanie biegu, odrzucenie, duplikat'],
    ['sprawy.wstrzymanie', 'Wstrzymanie i wznowienie sprawy'],
    ['sprawy.zamkniecie', 'Zamknięcie sprawy (gdy warunki spełnione)'],
    ['sprawy.zamkniecie_wyjatkowe', 'Zamknięcie mimo niespełnionych warunków (z uzasadnieniem)'],
    ['sprawy.wznowienie', 'Ponowne otwarcie zamkniętej sprawy'],
    ['obieg.przydzielanie', 'Przydzielanie i przekazywanie cudzych zadań'],
    ['obieg.konfiguracja', 'Ustawienia typów zadań (komu domyślnie, terminy)'],
    ['zastepstwa.zarzadzanie', 'Zastępstwa innych osób'],
  ]),
  ...g('Oferty i katalog', [
    ['oferty.podglad', 'Podgląd ofert'],
    ['oferty.edycja', 'Tworzenie i edycja oferty roboczej'],
    ['oferty.wydanie', 'Wydanie oferty'],
    ['oferty.wydanie_ponizej_progu', 'Wydanie oferty poniżej progu rabatu/marży'],
    ['oferty.udostepnianie', 'Wysyłka, link, unieważnienie linku'],
    ['oferty.wycofanie', 'Wycofanie wersji, wystawienie w innej firmie'],
    ['oferty.akceptacja_reczna', 'Zanotowanie akceptacji ręcznej'],
    ['katalog.podglad', 'Katalog — podgląd (bez cen)'],
    ['katalog.edycja', 'Katalog — edycja pozycji i cennika'],
    ['katalog.cena_bazowa', 'Zapis ceny bazowej (zakupu) na karcie produktu, także z kalkulacji oferty'],
  ]),
  ...g('Widoczność danych finansowych', [
    ['ceny.sprzedaz', 'Ceny i wartości sprzedaży'],
    ['ceny.marza', 'Marża procentowa'],
    ['ceny.zakup', 'Ceny zakupu, koszty, marża kwotowa'],
  ]),
  ...g('Umowy, dokumenty, finanse', [
    ['umowy.podglad', 'Podgląd umów i dokumentów handlowych'],
    ['umowy.edycja', 'Przygotowanie i edycja umowy roboczej'],
    ['umowy.wydanie', 'Wydanie umowy do podpisu'],
    ['umowy.podpis', 'Odnotowanie podpisu (skan)'],
    ['umowy.podpis_elektroniczny', 'Wysyłka umowy do podpisu elektronicznego'],
    ['umowy.reprezentacja', 'Podpis elektroniczny w imieniu firmy (reprezentacja)'],
    ['umowy.aneks', 'Aneks'],
    ['umowy.anulowanie', 'Anulowanie / rozwiązanie umowy'],
    ['szablony.edycja', 'Szablony dokumentów — edycja'],
    ['finanse.podglad', 'Podgląd harmonogramów i wpłat'],
    ['finanse.wplaty', 'Rejestracja wpłaty'],
    ['finanse.storno', 'Storno wpłaty'],
    ['faktury.podglad', 'Podgląd faktur'],
    ['faktury.wystawianie', 'Wystawianie faktur (zaliczkowe, końcowe) w systemie fakturowym'],
    ['faktury.korekta', 'Faktury korygujące'],
    ['zakupy.podglad', 'Faktury zakupu (KSeF) — podgląd'],
    ['zakupy.obsluga', 'Faktury zakupu — sprawdzenie, księgowanie, zapłata, przypisanie do sprawy, pobranie z KSeF'],
    ['warunki.podglad', 'Podgląd stanu warunków startu (bez kwot)'],
    ['warunki.odstepstwo', 'Odstępstwo od warunków startu'],
  ]),
  ...g('Magazyn i zakupy', [
    ['magazyn.podglad', 'Stany magazynowe, ruchy i dokumenty (wg firm i magazynów)'],
    ['magazyn.ruchy', 'Przyjęcia, wydania, zwroty, przesunięcia'],
    ['magazyn.korekty', 'Korekty, odwrócenia dokumentów, inwentaryzacja'],
    ['magazyn.konfiguracja', 'Magazyny, grupy produktów, ustawienia magazynu'],
    ['magazyn.rezerwacje', 'Ręczne rezerwacje, zwolnienia i przeniesienia rezerwacji'],
    ['magazyn.kompletacja', 'Pakiety dla ekip: przygotowanie, kompletacja, gotowość do odbioru'],
    ['zakupy.zamowienia', 'Zamówienia do dostawców: przygotowanie, przyjęcie dostaw, powiązanie z fakturami'],
    ['zakupy.zlozenie', 'Potwierdzenie złożenia zamówienia u dostawcy'],
    ['dostawcy.edycja', 'Dostawcy: dodawanie i zmiana danych'],
    ['zapotrzebowania.podglad', 'Zapotrzebowania, rezerwacje i braki realizacji (bez wartości)'],
    ['zapotrzebowania.zglaszanie', 'Zgłaszanie i zmiana zapotrzebowania materiałów do zadania'],
  ]),
  ...g('Planowanie i kalendarz', [
    ['kalendarz.podglad', 'Podgląd kalendarza i planu ekip'],
    ['planowanie.edycja', 'Planowanie zadań, przydziały, terminy'],
    ['planowanie.konflikt', 'Zapis mimo konfliktu'],
    ['ekipy.zarzadzanie', 'Zarządzanie ekipami i składem'],
    ['nieobecnosci.wniosek', 'Wniosek o nieobecność'],
    ['nieobecnosci.zatwierdzanie', 'Zatwierdzanie nieobecności'],
    ['kalendarz.wpisy', 'Wydarzenia firmowe i wpisy organizacyjne'],
  ]),
  ...g('Wykonanie', [
    ['zadania.podglad', 'Podgląd zadań'],
    ['zadania.status', 'Rozpoczęcie, zakończenie, zmiana statusu'],
    ['zadania.dokumentacja', 'Czynności, notatki, zdjęcia'],
    ['zadania.materialy', 'Zużyte materiały'],
    ['zadania.blokada', 'Zgłoszenie blokady'],
    ['blokady.rozwiazanie', 'Rozwiązanie blokady'],
    ['protokoly.podpis', 'Protokół: przygotowanie i podpis klienta'],
    ['protokoly.uniewaznienie', 'Unieważnienie protokołu'],
    ['protokoly.wysylka', 'Ponowna wysyłka protokołu'],
  ]),
  ...g('Serwis', [
    ['serwis.podglad', 'Podgląd zgłoszeń serwisowych'],
    ['serwis.tworzenie', 'Tworzenie zgłoszeń'],
    ['serwis.platnosci', 'Rozliczenie serwisu: cennik, pozycje do zapłaty, płatność klienta na miejscu (ING Pay)'],
    ['przeglady.edycja', 'Plany przeglądów — edycja'],
  ]),
  ...g('Flota', [
    ['flota.podglad', 'Flota — karta pojazdu, terminy, przebieg, zgłoszenia, opony, niedostępność (bez kosztów)'],
    ['flota.zarzadzanie', 'Flota — pojazdy, przypisania, czynności i wizyty, obsługa zgłoszeń, opony, przebieg, niedostępność, dokumenty'],
    ['flota.koszty', 'Flota — koszty, paliwo, przypisanie faktur zakupu do pojazdów, dokumenty wrażliwe (polisy, umowy)'],
    ['flota.import', 'Flota — import transakcji paliwowych i ich wyjaśnianie'],
    ['flota.przypomnienia', 'Flota — odbiorca automatycznych przypomnień (w zakresie firm)'],
    ['flota.dostepnosc', 'Flota — dostępność pojazdów w kalendarzu i ostrzeżenia o konflikcie w planowaniu'],
  ]),
  ...g('Zestawienia', [['zestawienia.podglad', 'Zestawienia: sprzedaż, finanse, marża (wg firm z zakresu)']]),
  ...g('Administracja', [
    ['pracownicy.podglad', 'Pracownicy — podgląd listy i karty pracownika'],
    ['uzytkownicy.zarzadzanie', 'Pracownicy (konta, karta pracownika), role, przypisanie firm, prawa indywidualne'],
    ['urzadzenia_mobilne.uniewaznienie', 'Urządzenia mobilne — unieważnienie'],
    ['firmy.edycja', 'Firmy, numeracja, ustawienia dokumentów'],
    ['moduly.konfiguracja', 'Moduły dodatkowe (Flota, Magazyn) — włączanie i wyłączanie dla całego systemu'],
    ['integracje.konfiguracja', 'Integracje (Fakturownia) — konfiguracja i token'],
    ['procesy.edycja', 'Szablony procesów, typy zadań, listy czynności, protokoły'],
    ['audyt.podglad', 'Podgląd historii operacji'],
  ]),
];

export type PermissionCode = string;

export const PERMISSION_CODES: ReadonlySet<string> = new Set(PERMISSIONS.map((p) => p.code));
const PERMISSION_BY_CODE: ReadonlyMap<string, PermissionDef> = new Map(PERMISSIONS.map((p) => [p.code, p]));
export const permissionDef = (code: string): PermissionDef | undefined => PERMISSION_BY_CODE.get(code);

/** Dane firmowe: zakres „wszystkie” zawęża się do firm pracownika (gdy działa w wybranych firmach). */
export const isCompanyPermission = (code: string): boolean => PERMISSION_BY_CODE.get(code)?.company ?? false;
export const COMPANY_PERMISSION_CODES: readonly string[] = PERMISSIONS.filter((p) => p.company).map((p) => p.code);

/*
 * Podziału na role „terenowe” i „biurowe” nie ma (29.10.2026): każdy pracownik korzysta z aplikacji mobilnej,
 * a o tym, co widzi i robi, decydują wyłącznie uprawnienia (role i wyjątki pracownika).
 * Konto bez żadnego modułu panelu pracuje tylko w aplikacji mobilnej (/teren).
 */

export interface RolePermission {
  permission: string;
  scope: Scope;
}

/** Zakresy możliwe dla uprawnienia w edytorze (moduł — tylko W). Zakresu F nie nadaje się ręcznie. */
export const scopesFor = (permission: string): readonly Scope[] => PERMISSION_BY_CODE.get(permission)?.scopes ?? ['W'];

/** Czy para uprawnienie–zakres jest dopuszczalna (znany kod, zakres pasujący do uprawnienia). */
function checkPair(permission: string, scope: Scope): string | null {
  const def = PERMISSION_BY_CODE.get(permission);
  if (!def) return `Nieznane uprawnienie: ${permission}`;
  if (!SCOPES.includes(scope)) return `Nieznany zakres „${scope}” dla ${permission}`;
  if (scope === 'F') return `„${def.label}”: zakresu „firmy pracownika” nie nadaje się ręcznie — wybierz „wszystkie”, a firmy ustaw na karcie pracownika`;
  if (!def.scopes.includes(scope)) return `„${def.label}”: zakres „${SCOPE_LABELS[scope]}” nie ma zastosowania (możliwe: ${def.scopes.map((s) => SCOPE_LABELS[s]).join(', ')})`;
  return null;
}

/**
 * Zawężenie do firm pracownika: przy wybranych firmach zakres W uprawnień firmowych zamienia się w F.
 * Te same reguły liczy widok `user_effective_permissions` (tabela company_permissions).
 */
export function narrowToCompanies(perms: ReadonlyMap<string, ReadonlySet<Scope>>, allCompanies: boolean): Map<string, Set<Scope>> {
  const out = new Map<string, Set<Scope>>();
  for (const [p, s] of perms) {
    const set = new Set(s);
    if (!allCompanies && set.has('W') && isCompanyPermission(p)) {
      set.delete('W');
      set.add('F');
    }
    out.set(p, set);
  }
  return out;
}

// ── prawa nadawane bezpośrednio pracownikowi (poza rolą) ─────────────────────

/** `nadane` — dodaje uprawnienie do sumy z ról i funkcji; `odebrane` — odbiera je, nawet gdy daje je rola lub funkcja (odebranie wygrywa). */
export const USER_PERMISSION_EFFECTS = ['nadane', 'odebrane'] as const;
export type UserPermissionEffect = (typeof USER_PERMISSION_EFFECTS)[number];

export interface UserPermissionEntry {
  permission: string;
  scope: Scope;
  effect: UserPermissionEffect;
}

/** Walidacja praw indywidualnych: znane kody, zakres pasujący do rodzaju, bez powtórzeń. */
export function validateUserPermissions(entries: readonly UserPermissionEntry[]): string[] {
  const errors: string[] = [];
  const seen = new Set<string>();
  for (const { permission, scope } of entries) {
    const bad = checkPair(permission, scope);
    if (bad) {
      errors.push(bad);
      continue;
    }
    const k = `${permission}:${scope}`;
    if (seen.has(k)) errors.push(`Uprawnienie ${permission} (${SCOPE_LABELS[scope]}) podano dwa razy`);
    seen.add(k);
  }
  return errors;
}

/** Uprawnienia efektywne: suma (ról i funkcji), plus prawa nadane, minus prawa odebrane (odebranie wygrywa). */
export function applyUserPermissions(base: ReadonlyMap<string, ReadonlySet<Scope>>, entries: readonly UserPermissionEntry[]): Map<string, Set<Scope>> {
  const out = new Map<string, Set<Scope>>();
  for (const [p, s] of base) out.set(p, new Set(s));
  for (const e of entries) {
    if (e.effect !== 'nadane') continue;
    let set = out.get(e.permission);
    if (!set) out.set(e.permission, (set = new Set()));
    set.add(e.scope);
  }
  for (const e of entries) {
    if (e.effect !== 'odebrane') continue;
    const set = out.get(e.permission);
    if (!set) continue;
    set.delete(e.scope);
    if (!set.size) out.delete(e.permission);
  }
  return out;
}

/** Zwraca listę błędów (po polsku); pusta lista = rola poprawna. */
export function validateRolePermissions(perms: readonly RolePermission[]): string[] {
  const errors: string[] = [];
  for (const { permission, scope } of perms) {
    const bad = checkPair(permission, scope);
    if (bad) errors.push(bad);
  }
  return errors;
}

/**
 * Uprawnienia modułu magazynu (docs/14). Przy wyłączonym module serwer odbiera je wszystkim — ekrany, operacje,
 * linki i sekcje magazynu znikają, procesy działają jakby modułu nie było.
 */
export const WAREHOUSE_ONLY_PERMISSIONS: readonly string[] = ['zakupy.zamowienia', 'zakupy.zlozenie', 'dostawcy.edycja', 'modul.magazyn'];
export const isWarehousePermission = (code: string) => code.startsWith('magazyn.') || code.startsWith('zapotrzebowania.') || WAREHOUSE_ONLY_PERMISSIONS.includes(code);
/** Uprawnienia Floty (moduł opcjonalny, docs/19). */
export const isFleetPermission = (code: string) => code === 'modul.flota' || code.startsWith('flota.');
/** Uprawnienia widoczne w edytorach (role, prawa pracownika) — bez uprawnień modułów opcjonalnych wyłączonych w systemie. */
export const visiblePermissions = (features: { flota?: boolean; magazyn?: boolean } | null | undefined): readonly PermissionDef[] =>
  PERMISSIONS.filter((p) => (features?.magazyn || !isWarehousePermission(p.code)) && (features?.flota || !isFleetPermission(p.code)));
/** Rola magazyniera — ukryta przy wyłączonym module. */
export const WAREHOUSE_ROLE = 'magazynier';
